import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { CreateCategoryFormData, Category } from '../../types/category';
import { useCategories } from '../../hooks/useCategories';
import { 
  TagIcon, 
  CheckIcon, 
  FolderIcon,
  ChevronDoubleRightIcon 
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { useLanguage } from 'contexts/LanguageContext';

interface CategoryFormProps {
  isOpen: boolean;
  onCancel: () => void;
  initialData?: Category;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  isOpen,
  onCancel,
  initialData
}) => {
  const { categories, createCategory, updateCategory } = useCategories();
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<CreateCategoryFormData>({
    defaultValues: {
      name: '',
      parentId: null,
      isActive: true
    }
  });
  
  useEffect(() => {
    if (initialData) {
      setValue('name', initialData.name);
      setValue('parentId', initialData.parentId);
      setValue('isActive', initialData.isActive);
    } else {
      reset({
        name: '',
        parentId: null,
        isActive: true
      });
    }
  }, [initialData, setValue, reset]);

  const onSubmit = async (data: CreateCategoryFormData) => {
    try {
      setIsSubmitting(true);
      if (initialData) {
        await updateCategory(initialData.id, data);
      } else {
        await createCategory(data);
      }
      reset();
      setIsSubmitting(false);
      onCancel();
    } catch (error) {
      console.error('Error saving category:', error);
      setIsSubmitting(false);
    }
  };

  // Filter main categories for parent selection
  // When editing, exclude current category and its subcategories to prevent circular references
  const availableParents = categories.filter(category => {
    if (!initialData) return category.parentId === null;
    return category.parentId === null && category.id !== initialData.id;
  });

  // Meghatározzuk, hogy főkategóriát vagy alkategóriát szerkesztünk-e
  const isSubcategory = initialData ? initialData.parentId !== null : false;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? t('settings.categories.form.edit') : t('settings.categories.form.add')}
      size="xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-4">
        {/* Fejléc */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 p-4 rounded-lg mb-5">
          <div className="flex items-center">
            <div className={`text-white p-2 mr-3 rounded-lg shadow-md ${
              isSubcategory 
                ? 'bg-gradient-to-r from-amber-500 to-yellow-500'
                : 'bg-gradient-to-r from-orange-500 to-yellow-600'
            }`}>
              {isSubcategory ? (
                <div className="flex items-center">
                  <ChevronDoubleRightIcon className="h-5 w-5 mr-0.5" />
                  <TagIcon className="h-5 w-5" />
                </div>
              ) : (
                <FolderIcon className="h-6 w-6" />
              )}
            </div>
            <div>
              <h3 className="text-gray-800 dark:text-gray-200 font-medium text-md">
                {initialData ? t('settings.categories.form.editDetails') : t('settings.categories.form.addDetails')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isSubcategory 
                  ? 'Ez egy alkategória, amely egy főkategória alá tartozik.' 
                  : initialData 
                    ? 'Ez egy főkategória, amelyhez alkategóriákat lehet rendelni.'
                    : 'Hozzon létre egy új kategóriát vagy alkategóriát.'
                }
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-5">
          <div className="grid grid-cols-1 gap-4">
            {/* Név mező */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.categories.form.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register("name", { required: true })}
                className={`block w-full bg-white dark:bg-gray-700 border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg text-md py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors`}
                placeholder="Adja meg a kategória nevét..."
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{t('settings.categories.form.nameRequired')}</p>}
            </div>
            
            {/* Szülő kategória választó */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.categories.form.parent')}
              </label>
              <div className="relative">
                <select
                  {...register("parentId")}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-md py-2 pl-3 pr-10 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none"
                >
                  <option value="">{t('settings.categories.form.noParent')}</option>
                  {availableParents.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ha nem választ szülő kategóriát, ez egy főkategória lesz.
              </p>
            </div>
            
            {/* Státusz kapcsoló */}
            <div className="space-y-1 pt-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings.categories.form.status')}
              </label>
              <div className="flex items-center">
                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                  <input 
                    type="checkbox"
                    {...register("isActive")}
                    id="statusToggle"
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white dark:bg-gray-700 border-4 border-gray-300 dark:border-gray-600 appearance-none cursor-pointer transition-transform duration-200 ease-in checked:right-0 checked:border-green-500"
                  />
                  <label 
                    htmlFor="statusToggle" 
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 dark:bg-gray-600 cursor-pointer checked:bg-green-500"
                    style={{ transition: 'background-color 0.2s ease' }}
                  ></label>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.categories.form.active')}</span>
              </div>
              <style>
                {`
                .toggle-checkbox:checked + .toggle-label {
                  background-color: #10B981;
                }
                .toggle-checkbox:checked {
                  right: 0;
                  border-color: #10B981;
                }
                `}
              </style>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:focus:ring-offset-gray-800"
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className={`relative bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-2 text-sm rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800 ${
              isSubmitting ? 'opacity-80' : ''
            }`}
            disabled={isSubmitting}
          >
            <span className={`flex items-center justify-center ${isSubmitting ? 'invisible' : ''}`}>
              <CheckIcon className="h-4 w-4 mr-1.5" />
              {initialData ? t('settings.categories.form.update') : t('settings.categories.form.create')}
            </span>
            {isSubmitting && (
              <span className="absolute inset-0 flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoryForm; 